// @ts-strict-ignore
import {
  forwardRef,
  Inject,
  Injectable,
  NgZone,
  ViewContainerRef,
  Injector
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { SohoNotificationService, SohoToastService } from 'ids-enterprise-ng';
import { unescape } from 'lodash-es';
import { escape } from 'lodash-es';
import { isEmpty } from 'lodash-es';

import * as urltemplate from 'url-template';

import { AppConstants, LMInforStartModeStatus, LMUnifiedNavigationState } from './app.constants';
import { LMGlobalPersonalization } from '../states/models';
import { LMGlobalPersonalizationService } from '../states/services';
import { LMGlobalUtilService } from './lm-global-util.service';
import { LMActionFormDialogService, LMActionFormDialogResult, LMActionFormDialogResultCode } from '../components/modal/lm-action-form-dialog.service';
import {
  LMBusyIndicatorService,
  LMContextService,
  LMErrorUtilService,
  LMLoggerService,
  LMModalOpenService,
  LMSpecService,
  LMUrlInfoBuilderService,
  LMUrlUtilService
} from '../services';
import { LMBannerHeaderComponentRef, LMNavigationService } from '@app/components';
import { LMAppComponentService } from '@utils/lm-app-component.service';
import { ActionResource } from '@app/resources';
import { NavigationExtras, RouterStateSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { first } from 'rxjs/operators';

const LMContextUtilConstants = {
  PORTAL_MESSAGE_TIMEOUT: 5000 // how long to wait in ms for Portal drillback or favorite message on initial load
} as const;

export interface LMPortalTabOptions {
  hideMenu?: boolean;
  hideTitle?: boolean;
}

@Injectable()
export class LMContextUtilService {

  private isEnabled = false;
  private sentAppInitComplete = false;
  private logicalId: string;
  private webpartScreenId: string;
  private title: string;
  private lastBusinessContextMessage: any;
  private dialogPlaceholder: ViewContainerRef;
  private lmActionFormDialogService: LMActionFormDialogService;
  private startModeStatus$ = new BehaviorSubject<LMInforStartModeStatus>(LMInforStartModeStatus.NOT_STARTED);
  private startModeStatus = this.startModeStatus$.asObservable();
  private startModeTimer: any = null;
  private startModeParamValue: number = 0;
  private navigationReferrer$ = new BehaviorSubject<string | null>(null);
  public readonly navigationReferrer: Observable<string | null> = this.navigationReferrer$.asObservable();

  private bodReferenceFields = {
    DocumentID: { required: true, bodName: 'documentId', setName: 'DocumentID' },
    AccountingEntity: { required: false, bodName: 'accountingEntity', setName: 'AccountingEntity' },
    Location: { required: false, bodName: 'location', setName: 'Location' },
    Revision: { required: false, bodName: 'revisionId', setName: 'Revision' }
  };

  constructor(
    protected lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmSpecService: LMSpecService,
    private titleService: Title,
    private sohoToastService: SohoToastService,
    @Inject(forwardRef(() => LMErrorUtilService)) private lmErrorUtilService, // LMErrorUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private injector: Injector,
    private ngZone: NgZone,
    private http: HttpClient,
    protected lmAppComponentService: LMAppComponentService,
    private sohoNotificationService: SohoNotificationService,
    private lmBannerHeaderCompRef: LMBannerHeaderComponentRef,
    private lmModalOpenService: LMModalOpenService
  ) {

    this.webpartScreenId = this.lmContextService.getWebpartScreenId();
    this.logicalId = this.lmContextService.getLogicalId();

    this.isEnabled = (this.logicalId !== null && this.logicalId.length > 0);
    if (this.isEnabled) {
      this.webpartScreenId = (this.webpartScreenId) ? this.webpartScreenId : this.logicalId.split('.')[ 1 ];
      this.logicalId = unescape(this.logicalId);
      this.startModeParamValue = Number(this.getStartModeParam()) || 0;
      this.lmLoggerService.log([ 'LMContextUtilService', 'registeredMessageHandlers' ]);
      this.registerMessageHandlers();

      // Initial load in the Portal coming from a drillback or favorite.
      // Tell the Portal we are ready for messages. Delay routes while waiting for the message. Time out if it takes too long.
      if (this.startModeEnabled()) {
        this.lmLoggerService.log([ 'LMContextUtilService', 'inforStartMode', this.startModeParamValue ]);
        this.setStartModeStatus(LMInforStartModeStatus.STARTED);
        this.publishAppInitComplete();
        this.ngZone.runOutsideAngular(() => {
          this.startModeTimer = setTimeout(() => {
            this.lmLoggerService.log([ 'LMContextUtilService', 'inforStartMode timed out' ]);
            this.setStartModeStatus(LMInforStartModeStatus.TIME_OUT);
          }, LMContextUtilConstants.PORTAL_MESSAGE_TIMEOUT);
        });
      } else {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
      }
    }
  }

  public registerMessageHandlers() {
    // remove any existing favorite handler
    infor.companyon.client.unRegisterMessageHandler('showFavoriteContext');

    // register a bookmark (favorite or shortcut) handler
    infor.companyon.client.registerMessageHandler('showFavoriteContext', (data: any) => {
      this.lmLoggerService.log([ 'LMContextUtilService', 'showFavoriteContext', data ]);
      // replace initial home page url in browser history so browser back doesn't go to home route
      const extras = this.inStartMode() ? { replaceUrl : true } : undefined;
      // hide the bootstrap page overlay in case a route doesn't get run
      this.lmAppComponentService?.getAppComponent()?.hidePageOverlay();
      if (data === null) {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE_NO_ROUTE);
        return;
      }
      this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
      if (this.lmContextService.insideInforMobileHub()) {
        this.setNavigationReferrer(data.referrer); // Back to workspace navigation
      }
      // Always clear breadcrumbs except on browser refresh, where cached breadcrumbs should be preserved.
      const isBrowserRefresh = this.startModeParamValue === 3; // browser refresh, email link, browser bookmark
      if (this.lmContextService.insideInforMobileHub() || !isBrowserRefresh) {
        this.lmContextService.setClearNextPageState(true);
        this.lmContextService.setPortalNavigation(true);
      }
      this.startModeParamValue = 0; // reset so subsequent favorite requests clear breadcrumbs
      this.processFavoriteContext(data, extras);
    });

    // remove any existing drillback handler
    infor.companyon.client.unRegisterMessageHandler('applicationDrillback');

    // register a drillback handler
    infor.companyon.client.registerMessageHandler('applicationDrillback', (data: any) => {
      this.lmLoggerService.log([ 'LMContextUtilService', 'applicationDrillback', data ]);
      // replace initial home page url in browser history so browser back doesn't go to home route
      const extras = this.inStartMode() ? { replaceUrl : true } : undefined;
      // hide the bootstrap page overlay in case a route doesn't get run
      this.lmAppComponentService?.getAppComponent()?.hidePageOverlay();

      const drillbackIdentifier = 'drillbackURL=';
      const index = data.applicationDrillback.indexOf(drillbackIdentifier);
      let url = data.applicationDrillback.substring(index + drillbackIdentifier.length);

      // check for non encoded ampersand. Anything after that is not our url but meant for mingle/portal usage.
      const ampersandIndex = url.indexOf('&');
      if (ampersandIndex > -1) {
        url = url.substring(0, ampersandIndex);
      }

      if (this.lmContextService.inMingleWidget()) {
        // Widgets should not respond to drillback messages. They do still
        // need to register message handlers because of inforStartMode.
        this.lmLoggerService.log([ 'LMContextUtilService', 'registerMessageHandler', 'widget does not respond to drillback messages' ]);
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE_NO_ROUTE);
        return;
      }

      // todo: would really like to be able to rely on the drillbackUrl being fully encoded
      //  but with portal drillbacks I can't guarantee it. This causes me not to be able to
      //  use a hash for a selected panel of a composite form or page.
      // // get url param w/o decoding it.
      // const url = this.lmUrlUtilService.getUrlParam(data.applicationDrillback, 'drillbackURL');

      // is url a BI Drillback - LandmarkBIDrillback URL param from Infor Application View > ViewId "LandmarkBIDrillback"
      if (data.applicationDrillback.indexOf('LandmarkBIDrillback') > -1) {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
        this.processLandmarkBIDrillback(data);
        return;
      }

      if (data.applicationDrillback.indexOf('ProcessLinkDrillback') > -1) {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
        this.processLinkDrillbackRequest(decodeURIComponent(url));
        return;
      }

      // Use COMPLETE_NO_ROUTE so the home route loads, giving the dialog a base page.
      if (data.applicationDrillback.indexOf('RequestActionDrillback') > -1) {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE_NO_ROUTE);
        this.actionDrillbackRequest(decodeURIComponent(url));
        return;
      }

      if (data.applicationDrillback.indexOf('InboxNotificationDrillback') > -1) {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
        this.processInboxNotification(data);
        return;
      }

      if (data.applicationDrillback.indexOf('InboxWorkflowDrillback') > -1) {
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
        this.processInboxWorkflow(data);
        return;
      }

      // if (index === -1 || url === null || url === '') {
      if (!url) {
        this.lmLoggerService.log([ 'LMContextUtilService', 'registerMessageHandler', 'drillbackURL was empty or not provided' ]);
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE_NO_ROUTE);
        return;
      }

      if (data.applicationDrillback.indexOf('drillbackURL=') === -1) {
        // if no drillback is passed navigate to web/home.
        this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
        this.processSuiteHome();
        return;
      }

      this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);
      if (this.lmContextService.insideInforMobileHub()) {
        this.setNavigationReferrer(data.referrer); // Back to workspace navigation
      }
      // Clear page state. Start a fresh breadcrumb trail under a 'PORTAL' category.
      this.lmContextService.setClearNextPageState(true);
      this.lmContextService.setPortalNavigation(true);
      if (this.lmUrlUtilService.hasParam(decodeURIComponent(url), 'wfmStandalone')) {
        this.lmContextService.setIsWFMStandaloneWebapp(true);
      }
      this.processStandardDrillbackRequest(decodeURIComponent(url), extras);
    });

    infor.companyon.client.unRegisterMessageHandler('inforUnifiedBeforeNavigation');

    infor.companyon.client.registerMessageHandler('inforUnifiedBeforeNavigation', (data) => {
      // unified menu navigation request came from the Portal
      this.lmLoggerService.log([ 'LMContextUtilService', 'inforUnifiedBeforeNavigation', data ]);
      this.setStartModeStatus(LMInforStartModeStatus.COMPLETE);

      // wait while we do dirty logic - this message needs to be sent within 100 ms or the navigation will time out
      this.sendUnifiedNavigationMessage(data.id, LMUnifiedNavigationState.PENDING);

      const currentComponent = this.lmAppComponentService?.getAppComponent()?.getRouteComponent();
      if (currentComponent?.allowPageNavigation) {
        currentComponent.allowPageNavigation(undefined, true).then((result) => {
          const state = (result) ? LMUnifiedNavigationState.CONTINUE : LMUnifiedNavigationState.BLOCK;
          this.sendUnifiedNavigationMessage(data.id, state);
        }).catch((error: any) => { // if the dirty check throws an error, allow the navigation or the unified menu will be dead
          this.lmLoggerService.error([ 'LMContextUtilService', 'Error calling allowPageNavigation()', error ]);
          this.sendUnifiedNavigationMessage(data.id, LMUnifiedNavigationState.CONTINUE);
        });
      } else {
        this.sendUnifiedNavigationMessage(data.id, LMUnifiedNavigationState.CONTINUE);
      }
    });

    infor.companyon.client.unRegisterMessageHandler('overlay.onComplete');

    infor.companyon.client.registerMessageHandler('overlay.onComplete', (data) => {
      // user clicked 'x' icon on Portal overlay modal
      this.lmLoggerService.log([ 'LMContextUtilService', 'overlay.onComplete', data ]);
      const currentComponent = this.lmAppComponentService?.getAppComponent()?.getRouteComponent();
      if (currentComponent?.onPortalOverlayClickCancel) {
        currentComponent.onPortalOverlayClickCancel();
      } else {
        this.sendOverlayCompleteMessage();
      }
    });

    infor.companyon.client.unRegisterMessageHandler('mobile_reloadPage');

    infor.companyon.client.registerMessageHandler('mobile_reloadPage', () => {
      // user pulled down on the mobile app to refresh the page
      const component = this.lmAppComponentService?.getAppComponent()?.getRouteComponent();
      if (component?.canRefresh?.()) {
        component.runRefresh();
        this.lmLoggerService.log([ 'LMContextUtilService', 'mobile_reloadPage', 'refresh triggered' ]);
      } else {
        this.lmLoggerService.log([ 'LMContextUtilService', 'mobile_reloadPage', 'refresh not available' ]);
      }
    });

    infor.companyon.client.unRegisterMessageHandler('mobile_previousPage');

    infor.companyon.client.registerMessageHandler('mobile_previousPage', () => {
      // user swiped from left on the mobile app to go to previous page
      this.lmLoggerService.log([ 'LMContextUtilService', 'mobile_previousPage' ]);
      this.lmBannerHeaderCompRef?.instance?.goBackInBreadCrumbStack();
    });

    infor.companyon.client.unRegisterMessageHandler('mobile_dismissOverlay');

    infor.companyon.client.registerMessageHandler('mobile_dismissOverlay', () => {
      // user swiped from left to close a modal dialog
      this.lmLoggerService.log([ 'LMContextUtilService', 'mobile_dismissOverlay' ]);
      if (this.lmModalOpenService.hasOpenModals) {
        if (!this.lmActionFormDialogService) {
          this.lmActionFormDialogService = this.injector.get(LMActionFormDialogService);
        }
        // If the last modal is an action form dialog, use its dismiss logic to handle dirty state.
        if (this.lmActionFormDialogService.hasOpenDialog &&
            this.lmActionFormDialogService.getOpenDialog === this.lmModalOpenService.lastModalRef) {
          this.lmActionFormDialogService.dismissOpenDialog();
        } else {
          this.lmModalOpenService.closeLastModal();
        }
      }
    });
  }

  private getStartModeStatus(): LMInforStartModeStatus {
    return this.startModeStatus$.getValue();
  }

  private setStartModeStatus(value: LMInforStartModeStatus): void {
    if (value === LMInforStartModeStatus.COMPLETE || value === LMInforStartModeStatus.COMPLETE_NO_ROUTE || value === LMInforStartModeStatus.TIME_OUT) {
      clearTimeout(this.startModeTimer);
      this.startModeTimer = null;
    }
    this.startModeStatus$.next(value);
  }

  // Indicates whether a Portal drillback or favorite message is coming after we send appInitComplete.
  // 0 – normal
  // 1 – favoritecontext
  // 2 – drillback
  // 3 - favoritecontext - external URL
  // 4 - drillback - external URL
  private getStartModeParam(): any | null {
    return this.lmUrlUtilService.getUrlParam(window.location.href, 'inforStartMode');
  }

  public startModeEnabled(): boolean {
    if (this.lmContextService.useInforStartMode()) {
      const paramValue = this.getStartModeParam();
      if (paramValue && paramValue > 0 && paramValue < 5) {
        return true;
      }
    }
    return false;
  }

  public inStartMode(): boolean {
    return (this.getStartModeStatus() === LMInforStartModeStatus.STARTED);
  }

  // Waits for a Portal drillback or favorite message on initial load.
  // Returns true if a message is received, false on timeout.
  public waitForPortalMessage(state: RouterStateSnapshot): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.startModeStatus.pipe(
          first(value => value === LMInforStartModeStatus.COMPLETE || value === LMInforStartModeStatus.COMPLETE_NO_ROUTE || value === LMInforStartModeStatus.TIME_OUT)
      ).subscribe((value: LMInforStartModeStatus) => {
        this.lmLoggerService.log([ 'LMContextUtilService', 'waitForPortalMessage', value ]);
        if (value === LMInforStartModeStatus.COMPLETE) {
          // Portal message received. Drillback handler navigates to an Angular route, so cancel the home page route.
          resolve(true);
        } else if (value === LMInforStartModeStatus.COMPLETE_NO_ROUTE) {
          // Portal message received but the drillback handler does not navigate to an Angular route (e.g. RequestActionDrillback opens a modal dialog).
          // Allow the home page route to load so there is a base page component underneath the dialog.
          resolve(false);
        } else {
          const jsonLog = {
            message: `LMCLIENT UI ALERT: START MODE TIMEOUT: Portal message not received within ${LMContextUtilConstants.PORTAL_MESSAGE_TIMEOUT} ms.`,
            useInforStartMode: this.lmContextService.useInforStartMode(),
            isUnified: this.lmContextService.isUnified(),
            inforStartMode: this.startModeParamValue,
            routeUrl: state.url
          };
          console.error(jsonLog);
          this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
          this.sohoNotificationService.show({
            type: 'error',
            message: Soho.Locale.translate('ErrorRequestSlow'),
            linkText: ''
          });
          resolve(false);
        }
      });
    });
  }

  public clearAll() {
    // console.log('clearAll');
    if (!this.isEnabled) {
      return;
    }

    this.lmLoggerService.log([ 'LMContextUtilService', 'clearAll' ]);
    infor.companyon.client.sendMessage('clearAllContext', {});
  }

  public disableShortcut() {
    if (!this.isEnabled) {
      return;
    }

    const data = {
      name: '',
      description: '',
      absoluteURL: 'false',
      shortcutContext: ''
    };

    this.publishShortcut(data);
  }

  public enableShortcut(params: any, performClearAll = true) {
    // console.log('enableShortcut performClearAll = ' + performClearAll);
    this.lmLoggerService.log([ 'LMContextUtilService', 'enableShortcut', params ]);

    // params: name, description, shortcutContext, absoluteURL
    if (!this.isEnabled) {
      return;
    }

    if (performClearAll) {
      this.clearAll();
    }

    if (params.absoluteURL === undefined || params.absoluteURL === null) {
      params.absoluteURL = 'false';
    }

    this.publishShortcut(params);
  }

  private createStandardShortcutParams(title = this.titleService.getTitle(), url = window.location.href): any {
    const logicalIdParams = this.lmUrlUtilService.getPrefixedParams(url, 'LogicalId');

    let shortcutUrl = '';
    if (isEmpty(logicalIdParams)) {
      // ----------------------------------------------------------
      // This code will ensure that adding a LogicalId to the url
      // params won't cause it to be placed after any #hash on
      // the url. For example if there is a page panel using a
      // hash to navigate directly to a tab in the page.
      // ----------------------------------------------------------
      const newUrl = new URL(url);
      newUrl.searchParams.set('LogicalId', this.logicalId);
      shortcutUrl = newUrl.href;
    } else {
      shortcutUrl = url;
    }

    const params = {
      name: title,
      description: '',
      absoluteURL: 'false',
      shortcutContext: shortcutUrl
    };

    return params;
  }

  public enableStandardShortcut(performClearAll = true, title = '') {
    if (!this.isEnabled) {
      return;
    }

    if (!this.sentAppInitComplete) {
      this.publishAppInitComplete();
    }

    this.lmLoggerService.time({ id: 'LMContextUtilService.enableStandardShortcut()' });

    this.title = title ? title : this.titleService.getTitle();
    const params = this.createStandardShortcutParams(this.title);
    this.enableShortcut(params, performClearAll);
    this.lmLoggerService.timeEnd('LMContextUtilService.enableStandardShortcut()');
  }

  public enablePreviousShortcut(title = '', url = '') {
    if (!this.isEnabled || !url) {
      return;
    }

    url = this.lmUrlUtilService.toFullUrl(url);
    const params = this.createStandardShortcutParams(title, url);

    this.publishPreviousShortcut(params);
  }

  public disablePreviousShortcut() {
    if (!this.isEnabled) {
      return;
    }

    const data = {
      name: '',
      description: '',
      absoluteURL: 'false',
      shortcutContext: ''
    };

    this.publishPreviousShortcut(data);
  }

  private publishPreviousShortcut(data: any) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishPreviousShortcut', data ]);
    infor.companyon.client.sendMessage('setPreviousShortcutContext', data);
  }

  public sendDrillbackMessage(url: string) {
    if (this.isEnabled && (this.lmContextService.inMingleWidget() || this.lmContextService.drillAroundWindow())) {
      const drillbackUrl = '?LogicalId=' + escape(this.logicalId) + '&drillbackURL=' + encodeURIComponent(url);
      this.publishDrillbackMessage(drillbackUrl);
    }
  }

  private getCustomHTML() {
    return '<p style="color:red;">Use this to clear the selected Index textbox</p>tt<a href="http://www.google.com">Google</a>tt';
  }

  public getLocation(href) {
    const l = document.createElement('a');
    l.href = href;
    return l;
  }

  public sendContextMessages(params: any) {
    // params: screenId, messageText, entities, standard
    if (!this.isEnabled) {
      return;
    }

    setTimeout(() => {
      this.lmLoggerService.time({ id: 'LMContextUtilService.sendContextMessages()' });

      for (const name in params.standard) {
        if (params.standard.hasOwnProperty(name)) {
          this.publishStandardContext(name, params.standard[ name ]);
        }
      }

      if (params.entities.length > 0) {
        const changeNoun = params.entities[0].bodReference ? true : false;
        const noun = changeNoun ? params.entities[0].bodReference.noun : '';
        if (!params.noDrillbackUrl) {
          for (let i = 0, entityLen = params.entities.length; i < entityLen; i++) {
            const entity = params.entities[ i ];
            entity.drillbackURL = '?LogicalId=' + escape(this.logicalId) + '&drillbackURL=' + encodeURIComponent(window.location.href);
          }
        }

        const data: any = {
          screenId: this.webpartScreenId + '_' + params.screenId,
          logicalId: this.logicalId,
          originatingTime: new Date().getTime(),
          entities: params.entities,
        };

        if (params.messageText.length > 0) {
          data.messageText = params.messageText;
        }

        if (!params.doNotPersistMessage) {
          this.lastBusinessContextMessage = data;
        }
        this.publishBusinessContext(data);
      }

      this.lmLoggerService.timeEnd('LMContextUtilService.sendContextMessages()');
    });
  }

  public sendMessagesForConfigHome(params: any) {
    // params: dataArea, businessClass, title
    const dataArea: string = params.dataArea || 'home';
    const businessClass: string = params.businessClass || '';
    const title: string = params.title || '';
    const resourceType = AppConstants.RESOURCETYPE_HOME;

    this.enableStandardShortcut(true, title);

    const entities = [ {
      entityType: dataArea,
      id1: this.title
    } ];

    this.sendContextMessages({
      screenId: resourceType + '_' + businessClass,
      messageText: Soho.Locale.translate('Sharing') + ' ' + this.title + ' ',
      entities,
    });
  }

  public sendMessagesForLandingPage(params: any) {
    // params: dataArea, landingPage, title
    const dataArea: string = params.dataArea || 'land';
    const landingPage: string = params.landingPage || '';
    const title: string = params.title || '';
    const resourceType = AppConstants.RESOURCETYPE_LAND;

    this.enableStandardShortcut(true, title);

    const entities = [ {
      entityType: dataArea,
      id1: this.title
    } ];

    this.sendContextMessages({
      screenId: resourceType + '_' + landingPage,
      messageText: Soho.Locale.translate('Sharing') + ' ' + this.title + ' ',
      entities,
    });
  }

  public sendMessagesForHtmlPage(params: any) {
    // params: dataArea, webApp, title
    if (!this.isEnabled) {
      return;
    }

    const dataArea: string = params.dataArea || 'html';
    const webApp: string = params.webApp || '';
    const title: string = params.title || '';
    const resourceType = AppConstants.RESOURCETYPE_HTML;

    this.enableStandardShortcut(true, title);

    const entities = [ {
      entityType: dataArea,
      id1: this.title
    } ];

    this.sendContextMessages({
      screenId: resourceType + '_' + webApp + '_' + this.title,
      messageText: Soho.Locale.translate('Sharing') + ' ' + this.title + ' ',
      entities,
    });
  }

  public mergeContextMessages(params: any) {
    // params: sourceMsgs, newMsgs
    const sourceMsgs = params.sourceMsgs;
    const addMsgs = params.addMsgs;

    if (!sourceMsgs.entities || !addMsgs || !addMsgs.entities) {
      return sourceMsgs;
    }

    sourceMsgs.entities = sourceMsgs.entities.concat(addMsgs.entities);
    return sourceMsgs;
  }

  public setDrillbackDialogElement(dialogElementRef: ViewContainerRef, lmActionFormDialogService: LMActionFormDialogService) {
    this.dialogPlaceholder = dialogElementRef;
    this.lmActionFormDialogService = lmActionFormDialogService;
  }

  public buildContextMessages(params: any): Observable<any> {
    const busFormName: string = params.busFormName || '';
    const busClassName: string = params.busClassName || '';
    const widgetEventPrefix: string = params.widgetEventPrefix;
    const dataObj: any = params.dataObj;
    const rows: any = params.rows || null;
    const activeRow: any = (params.activeRow !== undefined) ? params.activeRow : null;
    const busContextMessages: any = params.busContextMessages || null;
    const busContextInvocations: any = params.busContextInvocations || null;
    const bodReferenceFields = this.bodReferenceFields;

    let noun = '';
    const aryStandard: any = {};
    let aryEntities: Array<any> = [];
    return new Observable(observer => {

      if (!this.isEnabled) {
        observer.next({ status: AppConstants.STATUS_CANCEL });
        observer.complete();
        return;
      }

      setTimeout(() => {
        this.lmLoggerService.time({ id: 'LMContextUtilService.buildContextMessages()' });

        if (busContextInvocations && busContextMessages) {
          for (let i = 0, invocationLen = busContextInvocations.length; i < invocationLen; i++) {
            const invocation: any = busContextInvocations[ i ];
            const message: any = busContextMessages[ invocation.name ];

            if (!message) {
              continue;
            }

            const data: any = buildContextMessage(message);

            if (message.businessEntity === 'inforBusinessContext') {
              aryEntities = aryEntities.concat(data.entity);
            } else {
              aryStandard[ message.businessEntity ] = data;
            }
          }
        }

        // An empty list sends the default business context message
        if (widgetEventPrefix === AppConstants.RESOURCETYPE_LIST && dataObj && (dataObj.data === undefined || dataObj.data.length === 0)) {
          aryEntities = [];
        }

        if (aryEntities.length === 0) {
          const entity: any = {
            entityType: this.lmContextService.getUrlPdlOrDa(),
            id1: this.titleService.getTitle()
          };
          aryEntities.push(entity);
        }

        addBodReferenceToEntities();

        const retObj = {
          entities: aryEntities,
          standard: aryStandard,
          messageText: Soho.Locale.translate('Sharing') + ' ' + this.titleService.getTitle() + ' ',
          screenId: widgetEventPrefix + '_' + busClassName + (busFormName.length > 0 ? '_' + busFormName : ''),
          status: AppConstants.STATUS_COMPLETED
        };

        observer.next(retObj);
        observer.complete();

        this.lmLoggerService.timeEnd('LMContextUtilService.buildContextMessages()');
      });
    });

    // --------------------------------------------------
    // private methods
    // --------------------------------------------------
    function buildContextMessage(message: any) {
      const data: any = {};

      for (const key in message) {
        if (key === 'businessEntity') {
          continue;
        }

        const val = message[ key ];
        if (Array.isArray(val)) {
          if (key === 'properties') {
            const properties = processProperties(val, activeRow);
            Object.assign(data, properties);
          } else {
            const group = processGroupProperties(val, key, message.businessEntity);
            data[ key ] = group;
          }
        } else {
          data[ key ] = val;
        }
      }

      return data;
    }

    function processGroupProperties(group: any, groupName: string, businessEntity: string) {
      const aryGroup = [];
      const useSelectedRows = processMessageForSelectedRows(groupName, businessEntity);

      if (useSelectedRows) {
        const item = group[ 0 ];
        for (let i = 0, rowsLen = rows.length; i < rowsLen; i++) {
          aryGroup.push(processProperties(item, rows[ i ]));
        }
      } else {
        for (let i = 0, groupLen = group.length; i < groupLen; i++) {
          const item = group[ i ];
          aryGroup.push(processProperties(item, activeRow));
        }
      }

      return aryGroup;
    }

    function processProperties(properties: any, row: any) {
      const data: any = {};

      for (let i = 0, propertyLen = properties.length; i < propertyLen; i++) {
        const item: any = properties[ i ];
        let value = '';

        if (item.type === 'text') {
          value = item.value;
        } else if (item.type === 'field') {
          value = getFieldValue(item.fieldId, row);
        }

        if (item.property === 'bod reference.noun') {
          noun = value;
        } else {
          data[ item.property ] = value;
        }
      }

      return data;
    }

    function processMessageForSelectedRows(groupName: string, businessEntity: string) {
      if (!rows || rows.length === 1) {
        return false;
      }

      return ((businessEntity === 'multipleAddress' && groupName === 'Addresses')
              || (businessEntity === 'packageInformation' && groupName === 'TrackingNumbers'));
    }

    function getField(fieldId: string, r: any): LMField {
      const row: any = (r !== undefined) ? r : null;
      let field: any = null;

      if (dataObj && dataObj.fields) {
        if (dataObj.fields.hasOwnProperty(fieldId)) {
          field = dataObj.fields[ fieldId ];
        }
      } else if (dataObj && dataObj.data && row != null && dataObj.data.length > row) {
        if (dataObj.data[ row ].fields.hasOwnProperty(fieldId)) {
          field = dataObj.data[ row ].fields[ fieldId ];
        }
      }

      return field;
    }

    function getFieldValue(fieldId: string, r: any) {
      const row = (r !== undefined) ? r : null;
      let value = '';

      if (dataObj && dataObj.fields) {
        if (dataObj?.fields?.hasOwnProperty(fieldId)) {
          value = dataObj.fields[ fieldId ].value;
        }
      } else if (dataObj && dataObj.data && row !== null && dataObj.data.length > row) {
        if (dataObj.data[ row ]?.fields?.hasOwnProperty(fieldId)) {
          value = dataObj.data[ row ].fields[ fieldId ].value;
        }
      }

      return value;
    }

    function addBodReferenceToEntities() {
      const bodFieldId = busClassName + '_BODId';
      const bodId = getFieldValue(bodFieldId, activeRow);
      if (bodId.length === 0) {
        return;
      }

      const bodReference: any = {};
      const bodField: LMField = getField(bodFieldId, activeRow);

      bodField.members.map(memberId => ({
        reference: memberId.substring(memberId.lastIndexOf('_') + 1),
        memberValue: getFieldValue(memberId, activeRow)
      }))
        .filter(referenceObject => bodReferenceFields[ referenceObject.reference ])
        .forEach(referenceObject => {
          const bodName: any = bodReferenceFields[ referenceObject.reference ].bodName;
          bodReference[ bodName ] = referenceObject.memberValue;
        });

      if (noun.length === 0) {
        if (aryEntities.length > 0) {
          if (aryEntities[ 0 ].hasOwnProperty('entityType')) {
            const entityType = aryEntities[ 0 ].entityType;
            noun = entityType.replace(/^Infor/, '');
          }
        }

        if (noun.length === 0) {
          noun = busFormName;
        }
      }
      bodReference[ 'noun' ] = noun;

      for (let i = 0, entityLen = aryEntities.length; i < entityLen; i++) {
        aryEntities[ i ].bodReference = bodReference;
      }
    }
  }

  private processFavoriteContext(data: any, extras?: NavigationExtras): void {
    this.lmLoggerService.log([ 'LMContextUtilService', 'showFavoriteContext', data ]);

    if (data === null) {
      return;
    }
    let url = data.favoriteContext;
    if (url.indexOf('http%3A%2F%2F') === 0 || url.indexOf('https%3A%2F%2F') === 0) {
      // The url appears to be encoded. Apparently Mingle 11 encodes url using encodeURIComponent before sending the message.
      // But mingle 12 does not encode the URL.(LMCLIENT-33716)
      // So decode only when it is required to avoid errors like LMCLIENT-28847. In this ticket, the key field has '&'
      // and decoding the whole url will decode the key fields too.
      // lodash unescape was mistakenly used here. It does not work like regular unescape or decodeURIComponent (Remove lodash usage LMCLIENT-33757)
      // 3.5 uses regular unescape instead of decodeURIComponent
      url = decodeURIComponent(url);
    }

    // Replace foreign host with current host for cross-tenant bookmark portability.
    if (!this.lmUrlUtilService.urlHostMatchesWindowLocationHost(url)) {
      url = this.lmUrlUtilService.replaceHostWithCurrent(url);
      url = this.lmUrlUtilService.removeParams(url, [ 'csk.LogicalId' ]);
    }

    this.lmBusyIndicatorService.showBusyIndicator();
    this.processPortalTabUrl(url);
    this.navigateWebAppUrl(url, extras);
  }

  private processInboxNotification(data: any): void {
    // Expect Drill back to contain: ?prodline={ID1}&notificationID={ID2}&InboxNotificationDrillback=true&LogicalId={LogicalId}
    // '?prodline=hcm&notificationID=c7e5aace-e180-8a00-0000-002064354439&InboxNotificationDrillback=true&LogicalId=lid%3A%2F%2Finfor.lawson-ghr.ghr110-ade-useast'
    let drillback = data.applicationDrillback as string;
    if (!drillback || drillback.length === 0) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxNotification received no drillback data.' ]);
      return;
    }
    drillback = decodeURIComponent(drillback);
    if (drillback.startsWith('?')) {
      drillback = drillback.substring(1);
    }
    const params = {} as { InboxNotificationDrillback?: boolean, notificationID?: string, prodline?: string, LogicalId?: string };
    drillback.split('&').forEach(param => {
      const nameValue = param.split('=');
      params[nameValue[0]] = nameValue[1];
    });

    if (!params?.notificationID) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxNotification', 'Notification ID is not available in drillback data', data ]);
      return;
    }
    // Assumption: Mingle or portal will go to the homepage first before posted message is handled in registered handlers.
    // If so, the context is created and the UrlPdlOrDa and logicalId are set in context.
    const actionResource = new ActionResource({ busClassName: 'UserNotification', keyValueArray: [ params.notificationID ], actionName: '_open' });
    // const altWebApp = this.lmContextService.isUnified() ? 'web' : this.lmContextService.getWebApp();
    // const url = this.lmUrlUtilService.buildUrl({ webApp: altWebApp, resource: actionResource, params: { InboxNotificationDrillback: true } });
    const url = this.lmUrlUtilService.buildUrl({ resource: actionResource, params: { InboxNotificationDrillback: true } });
    this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxNotification', 'Navigating to notification', actionResource ]);
    this.navigateWebAppUrl(url, { skipLocationChange: true });
  }

  private processInboxWorkflow(data: any): void {
    // Expect Drill back to contain:  dataArea={ID1}&workUnit={ID2}&activity={ID3}&queueAssignment={ID4}&csk.showusingxi=true&InboxWorkflowDrillback=true
    // ?dataArea=QAD221_AX2_HCM&workUnit=12&activity=1&queueAssignment=&csk.showusingxi=true&InboxWorkflowDrillback=true
    let drillback = data.applicationDrillback as string;
    if (!drillback || drillback.length === 0) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow received no drillback data.' ]);
      return;
    }
    drillback = decodeURIComponent(drillback);
    if (drillback.startsWith('?')) {
      drillback = drillback.substring(1);
    }
    const params = {} as { InboxWorkflowDrillback?: boolean, workUnit?: string, activity?: string, queueAssignment?: string, dataArea?: string };
    drillback.split('&').forEach(param => {
      const nameValue = param.split('=');
      params[nameValue[0]] = nameValue[1];
    });

    if (!params?.workUnit) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow', 'Workunit is not available in drillback data', data ]);
      return;
    }

    const url = window.location.protocol + '//' + window.location.host + '/bpm/ionInboxLoad?dataArea=' + params.dataArea
      + '&workUnit='+ params.workUnit + '&activity=' + params.activity + '&queueAssignment=' + params.queueAssignment
      + '&csk.showusingxi=true&output=JSON&showHtml=false';
    this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow', 'Navigating to IonInboxLoad to get redirectUrl for Workunit ', params.workUnit ] );
    this.lmBusyIndicatorService.showBusyIndicator();
    this.http.get(url).pipe(first()).subscribe((response: any) => {
      if (!response.redirectUrl) {
        this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow', 'Navigating to IonInboxLoad to get redirectUrl for Workunit ', params.workUnit, 'failed.' ] );
        this.lmBusyIndicatorService.hideBusyIndicator();
        this.lmErrorUtilService.handleError(response.errorMessage);
        return;
      }
      // Go to action redirect URL returned by BPM servlet. This will be a URL to form for process flow approval.
      // Remove &csk.hidetitlebar=true&csk.hidemenubar=true&csk.mi=true (These three are required in Mingle Inbox container)
      // Add fromInbox=true (It will paint the action buttons on the top and Inbox Notifications uses it).
      let redirectUrl = response.redirectUrl;
      redirectUrl = this.lmUrlUtilService.removeParams(redirectUrl, [ 'csk.mi', 'csk.hidetitlebar', 'csk.hidemenubar', 'showHtml', 'output' ]);
      redirectUrl = this.lmUrlUtilService.addParamToUrl(redirectUrl, 'fromInbox', 'true');
      this.navigateWebAppUrl(redirectUrl);
    },
    error => {
      this.lmBusyIndicatorService.hideBusyIndicator();
      // this.lmErrorUtilService.handleUnexpectedError(error);
      this.lmLoggerService.error(error);
      return throwError(error || 'Server error');
    });
  }

  private processLandmarkBIDrillback(data: any): void {
    // is url a BI Drillback - LandmarkBIDrillback URL param from Infor Application View > ViewId "LandmarkBIDrillback"
    const urlPdlOrDa = this.lmContextService.getUrlPdlOrDa();
    let url = window.location.protocol + '//' + window.location.host + '/' + urlPdlOrDa + '/web/bidrill' + data.applicationDrillback;
    url = this.checkForDeprecatedWebapp(url);

    // do not decode LandmarkBIDrillback messages, causes issues parsing the dimension data by BIDrillController
    this.lmGlobalUtilService.navigate(url);
  }

  private processSuiteHome(): void {
    const urlPdlOrDa = this.lmContextService.getUrlPdlOrDa();
    let url = window.location.protocol + '//' + window.location.host + '/' + urlPdlOrDa + '/web/home';
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
    this.lmGlobalUtilService.navigate(url);
  }

  private processLinkDrillbackRequest(url: string) {
    this.lmBusyIndicatorService.showBusyIndicator();
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(url, 'process-link-drillback');

    // this.lmUrlUtilService.updateUrlParams(url, { output: 'spec' })
    //
    // if (url !== null) {
    //     url = url.replace('output=spec', '');
    //     this.navigateWebAppUrl(url);
    // }

    if (url === null || url.indexOf('/menu/') === 0) {
      this.navigateWebAppUrl(url);
      return;
    }

    url = this.checkForDeprecatedWebapp(url);
    this.lmSpecService.getApplicationMenuItemSpec(url).subscribe(response => {
      if (!response.dialogUrl) {
        this.navigateWebAppUrl(response.redirectUrl);
        return;
      }
      const actionName = this.lmUrlUtilService.getUrlParam(url, 'action');
      if (!actionName) {
        this.lmLoggerService.log('unable to find action param in url');
        return null;
      }

      // if a drillback comes in before the menu is loaded, lazily inject LMActionFormDialogService
      if (!this.lmActionFormDialogService) {
        this.lmActionFormDialogService = this.injector.get(LMActionFormDialogService);
      }

      this.lmSpecService.getFormSpecFromDialogActionUrl(response.dialogUrl).subscribe((formUrlResponse: LMFormSpecResponse) => {
        const formUrlInfo = formUrlResponse.formUrlInfo;
        const formSpec = formUrlResponse.formSpec;
        const actionSpec: LMActionSpec = formSpec.busClassSpec.actions.find(a => a.name === actionName);
        this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec).subscribe();
      });
    });
  }

  private actionDrillbackRequest(url: string) {
    this.lmBusyIndicatorService.showBusyIndicator();
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(url, 'action-drillback');

    url = this.lmUrlUtilService.updateUrlParams(url, { output: 'spec' });
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
    url = this.checkForDeprecatedWebapp(url);

    this.lmLoggerService.time({
      id: 'TotalActionForm',
      description: 'TOTAL ACTION FORM',
      className: 'LMContextUtilService',
      timingStart: 'LMContextUtilService -> actionDrillbackRequest()',
      timingEnd: 'LMFormContentComponent -> ngAfterViewInit()'
    });

    this.lmSpecService.getFormSpecFromRedirectActionUrl(url).subscribe((response: LMActionFormUrlResponse | LMSystemException) => {
      if ((response as LMSystemException).status
              && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
        this.lmErrorUtilService.handleError((response as LMSystemException).statusText, (response as LMSystemException).details);
        this.lmBusyIndicatorService.hideBusyIndicator();
        return;
      }

      // if a drillback comes in before the menu is loaded, lazily inject LMActionFormDialogService
      if (!this.lmActionFormDialogService) {
        this.lmActionFormDialogService = this.injector.get(LMActionFormDialogService);
      }

      const formUrlInfo = (response as LMActionFormUrlResponse).formUrlInfo;
      const formSpec = (response as LMActionFormUrlResponse).formSpec;
      const resourceId = (response as LMActionFormUrlResponse).resourceId;
      const action = (response as LMActionFormUrlResponse).action;
      const actionSpec: LMActionSpec = formSpec.busClassSpec.actions.find(a => a.name === action);

      const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
      const dataUrl = urlCompiledTemplate.expand({ resourceId, actionName: action });
      this.lmActionFormDialogService.getActionFormDialogDataView(dataUrl).subscribe((responseDV: any) => {
        const formDataView: LMDataView = responseDV;
        if (formDataView.status === AppConstants.STATUS_COMPLETED) {
          this.lmActionFormDialogService
            .runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView, null, null, true)
            .subscribe((result: LMActionFormDialogResult) => {
              // Resend the last context message to restore the screen id
              if (this.lastBusinessContextMessage) {
                this.publishBusinessContext(this.lastBusinessContextMessage);
              }

              if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                const actionResult = result.transactionStateResult.getTransactionResult();
                if (actionResult.status === AppConstants.STATUS_COMPLETED) {
                  if (!actionResult.actionSpec.suppressCompletionMessage) {
                    this.lmGlobalUtilService.displayToastMessage(actionSpec.label, actionResult.message, AppConstants.TRA_ACTION_COMPLETED);
                  }
                  if (actionResult?.isCompletionNavigation && actionResult?.redirectUrl) {
                    // using injector due to circular dependency with LMNavigationService
                    this.injector.get(LMNavigationService).runNavigation(actionResult.redirectUrl);
                  }
                }
              }
            });
        } else {
          this.lmBusyIndicatorService.hideBusyIndicator();
          this.lmLoggerService.error(responseDV);
          this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
        }
      });
    });
  }

  private processStandardDrillbackRequest(url: string, extras?: NavigationExtras): void {
    this.lmBusyIndicatorService.showBusyIndicator();
    this.processPortalTabUrl(url);
    url = this.lmUrlUtilService.toFullUrl(url); // is the drillback a relative url?
    url = this.lmUrlUtilService.remove3XParams(url);
    url = this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(url, 'standard-drillback');
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

    this.navigateWebAppUrl(url, extras);
  }

  private processPortalTabUrl(url: string): void {
    if (this.lmUrlUtilService.getUrlParam(url, 'pt') === 'true') { // in a portal tab
      // Ensure context session key updates trigger change detection
      this.ngZone.run(() => {
        this.lmContextService.setInPortalTab(true);
        this.lmUrlUtilService.addContextSessionKeysFromUrl(url);
      });
    }
  }

  private navigateWebAppUrl(url: string, extras?: NavigationExtras) {
    // todo use lm-url-util.service standar url path split function
    //  to get the parts.
    //  for example: this.lmUrlUtilService.getWebAppNameFromUrl(url);
    let resourceType: string;
    let webAppName: string;
    const urlParts = url.split('/');
    if (urlParts.length >= 7) {
      webAppName = urlParts [ 4 ];
      resourceType = urlParts[ 5 ];
      const lastURLSection = urlParts[ 6 ].split('?');
      let resourceId = lastURLSection[ 0 ];
      resourceId = resourceId.replace('.', '_prd_');

      let settings: LMGlobalPersonalization = {};
      const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
      if (!isEmpty(bannerPersonalization)) {
        settings = bannerPersonalization;
      }

      if (resourceType === 'menu') {
        settings.lastOpenedMenu = 'appmenu_' + resourceId;
      } else {
        if (!isEmpty(bannerPersonalization) && bannerPersonalization.lastOpenedMenu) {
          settings.lastOpenedMenu = '';
        }
      }
      this.lmGlobalPersonalizationService.saveGlobalPersonalization(settings);
    }

    if (resourceType && resourceType === 'land') {
      const webApp = (webAppName !== '') ? webAppName : this.lmContextService.getContext().webApp;
      url  =  '/' + this.lmContextService.getContext().urlPdlOrDa + '/' + webApp + '/home';
    }

    // todo for mingle drill backs this is already being called. consider remove one of them
    //  probably the one in the mingle drill back functions.
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

    // To aid webapp consolidation effort, check webapp is accessible, if not switch to context webapp
    url = this.checkForDeprecatedWebapp(url);

    // LMCLIENT-35952 must put back in Angular Zone as Ming.le is not running in the zone.
    // Change Detection was not firing for certain drillback urls.
    this.ngZone.run(() =>
      this.lmGlobalUtilService.navigateByUrl(url, extras));
  }

  private checkForDeprecatedWebapp(url: string): string {
    if (this.lmContextService.isUnified() && url.indexOf('inforUnified=1') > -1) {
      return url;
    }
    // All drillbacks will bring up the application first before navigating to the drillback url.

    // 1. Get the webapp part from url
    const webAppName = this.lmUrlUtilService.getWebAppNameFromUrl(url);
    // 2. Check against accessible webapps in context service for this webapp name
    const isAccessible = this.lmContextService.getAccessibleWebApps().some(w => w.webAppName === webAppName);
    // 3. If it is not accessible, then switch to the webapp in context service.
    // if viewing a unified app like EmployeeSelfService and clicking ctrl shift click and trying to create or view a
    // configuration it will open in a new Portal tab. It should not replace the webapp in the url in this case.
    const isConfigurationConsole = webAppName === 'ConfigurationConsole' && this.lmContextService.inPortalTab();
    if (!isAccessible && !isConfigurationConsole) {
      url = this.lmUrlUtilService.switchWebAppName(url, this.lmContextService.getContext().webApp);
    }
    return url;
  }

  // Tells the Portal that we are ready to receive messages.
  private publishAppInitComplete() {
    this.lmLoggerService.log([ 'LMContextUtilService', 'appInitComplete' ]);
    this.sentAppInitComplete = true;
    infor.companyon.client.sendMessage('appInitComplete', window.name);
  }

  private publishBusinessContext(data: any) {
    // console.log('publishBusinessContext data=>');
    // console.log(data);
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishBusinessContext', data ]);
    infor.companyon.client.sendMessage('inforBusinessContext', data);
  }

  private publishDrillbackMessage(data: any) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishDrillbackMessage', data ]);
    infor.companyon.client.sendPrepareDrillbackMessage(data);
  }

  private publishShortcut(data: any) {
    // console.log('publishShortcut data=>');
    // console.log(data);
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishShortcut', data ]);
    infor.companyon.client.sendMessage('setShortcutContext', data);
  }

  public publishStandardContext(messageId: string, data: any) {
    // console.log('publishStandardContext messageId = ' + messageId + ' data=>');
    // console.log(data);
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishStandardContext', data ]);
    infor.companyon.client.sendMessage(messageId, data);
  }

  public openPortalTab(url: string, options?: LMPortalTabOptions) {
    const hideMenu = options?.hideMenu ?? true; // hide menubar by default
    const hideTitle = options?.hideTitle ?? false; // show titlebar by default
    url = this.lmUrlUtilService.addParamToUrl(url, 'pt', 'true');
    if (hideMenu) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'csk.hidemenubar', 'true');
    }
    if (hideTitle) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'csk.hidetitlebar', 'true');
    }
    this.lmLoggerService.log([ 'LMContextUtilService', 'openPortalTab', url ]);
    const data = {
      link: "?LogicalId=" + this.lmContextService.getLogicalId() + "&drillbackURL=" + encodeURIComponent(url),
      newTab: true
    };
    infor.companyon.client.sendMessage('prepareApplicationDrillback', data);
  }

  public setPortalTabTitle(title: string) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'setPortalTabTitle', title ]);
    const data = {
      tab: {
        title: title
      }
    };
    infor.companyon.client.sendMessage('inforApplicationUpdate', data);
  }

  public sendUnifiedNavigationMessage(id: string, state: LMUnifiedNavigationState) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'sendUnifiedNavigationMessage', 'id: ' + id + ', state: ' + state ]);
    infor.companyon.client.sendMessage('inforUnifiedConfirmNavigation', {
      id: id,
      state: state
    });
  }

  public selectUnifiedMenuItem(id: string) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'selectUnifiedMenuItem', 'id: ' + id ]);
    infor.companyon.client.sendMessage('inforUnifiedMenu', {
      op: 'select',
      id: id
    });
  }

  public setOverlayTitle(title: string) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'setOverlayTitle', title ]);
    infor.companyon.client.sendMessage('overlay.title', title);
  }

  public sendOverlayCompleteMessage() {
    this.lmLoggerService.log([ 'LMContextUtilService', 'sendOverlayCompleteMessage' ]);
    infor.companyon.client.sendMessage('overlay.complete', {});
  }

  public sendToastMessage(title: string, message: string ) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'sendToastMessage', title, message ]);
    const position = this.lmGlobalUtilService.getToastMessagePosition();
    infor.companyon.client.sendMessage('showToast', { title, message, position });
  }

  public resizeOverlay(width: number, height: number) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'resizeOverlay', 'width: ' + width +', height: ' + height ]);
    infor.companyon.client.sendMessage('overlay.resize', {
      size: { width: width, height: height },
    });
  }

  public showQRCodeScanner(): Observable<LMBarCodeData> {
    return new Observable<LMBarCodeData>(observer => {
      const data = { source: 'mobile', inputType: '1' };
      this.ngZone.runOutsideAngular(() => {
        infor.companyon.client.unRegisterMessageHandler('mobile_shareQRCodeData');
        infor.companyon.client.registerMessageHandler('mobile_shareQRCodeData', (data: LMBarCodeData) => {
          this.ngZone.run(() => {
            observer.next(data);
            observer.complete();
            infor.companyon.client.unRegisterMessageHandler('mobile_shareQRCodeData');
          });
        });
        infor.companyon.client.sendMessage('mobile_showQRCodeScanner', data);
      });
    });
  }

  // Show/hide the unified app menu in infor mobile app or mobile portal
  public toggleMobileAppMenu() {
    this.lmLoggerService.log([ 'LMContextUtilService', 'toggleMobileAppMenu' ]);
    infor.companyon.client.sendMessage('mobile_toggleAppMenu', '');
  }
  
  public getNavigationReferrer(): string | null {
    return this.navigationReferrer$.getValue();
  }

  public hasNavigationReferrer(): boolean {
    return this.navigationReferrer$.getValue() !== null;
  }

  public drillbackToNavigationReferrer(): void {
    if (this.hasNavigationReferrer()) {
      const referrer = this.getNavigationReferrer();
      this.lmLoggerService.log([ 'LMContextUtilService', 'drillbackToNavigationReferrer', referrer ]);
      this.publishDrillbackMessage(referrer);
      this.setNavigationReferrer(null);
    }
  }

  private setNavigationReferrer(referrer?: string | null): void {
    const value = referrer || null;
    this.lmLoggerService.log([ 'LMContextUtilService', 'setNavigationReferrer', value || 'cleared' ]);
    this.navigationReferrer$.next(value);
  }
}
